/**
 * CardHeader — THE one way a card starts (DESIGN-CALM-01 · Wave 2, Seth
 * 2026-09-22 "go"). Before this, one 360 page began its cards five ways:
 * RECENT SERVICE in whisper-caps on a grey band, "Messages & touchpoints" in
 * sentence case, tinted icon chips with a color wash, the rail's 11px
 * tracking labels. HubSpot's record cards and Salesforce's related lists
 * settled this years ago: title, count, one link, every card the same.
 *
 *   · a 16px slate icon (optional) — never tinted, never in a chip
 *   · the title at 14 semibold, Title Case (the caller writes it that way)
 *   · the count in grey right after the title
 *   · one action on the right — a link, or `right` for a small control
 *   · no band, no gradient, no sentence
 *
 * `variant="card"` (default) is the top of a Card: padding + hairline.
 * `variant="inline"` is just the row, for rail blocks with their own padding.
 */
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/cn";

export function CardHeader({
  icon: Icon,
  title,
  count,
  action,
  chip,
  right,
  variant = "card",
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  /** A count or short fact after the title (e.g. 3, "Sep 17"). */
  count?: number | string | null;
  /** The one link on the right. `arrow` adds the → that says "goes somewhere". */
  action?: { label: string; onClick: () => void; arrow?: boolean; title?: string };
  /** A small chip next to the title (scope, e.g. "all properties"). */
  chip?: ReactNode;
  /** Custom right-hand content when one link isn't the shape (Edit · Manage). */
  right?: ReactNode;
  variant?: "card" | "inline";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2",
        variant === "card" && "border-b border-hairline px-4 py-2.5",
        className,
      )}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />}
      <span className="min-w-0 truncate text-sm font-semibold text-slate-900">{title}</span>
      {count != null && count !== "" && (
        <span className="shrink-0 text-[13px] font-medium tabular-nums text-slate-400">{count}</span>
      )}
      {chip}
      {(action || right) && (
        <span className="ml-auto flex shrink-0 items-center gap-2.5">
          {right}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              title={action.title}
              className="inline-flex items-center gap-1 rounded text-[13px] font-semibold text-brand-700 transition-colors hover:text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
            >
              {action.label}
              {action.arrow && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </span>
      )}
    </div>
  );
}
